import { router } from '@inertiajs/react'

import { ConfirmDialog } from '@components/molecules/ConfirmDialog'
import AppErrorsAlert from '@molecules/Alerts/AppErrorsAlert'

import { scopedTranslation } from '@utils/I18n'

import { useShiftsStore } from './stores/useShiftsStore'

import { useArchiveShiftsMutation } from '../author_shifts/useArchiveShiftsMutation'
import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('roster_board')

type BulkDeleteDialogProps = {
  shifts: ShiftWithSlots[]
}

export function BulkDeleteDialog({ shifts }: BulkDeleteDialogProps) {
  const deleteTarget = useShiftsStore((state) => state.deleteTarget)
  const closeBulkDelete = useShiftsStore((state) => state.closeBulkDelete)
  const clearSelection = useShiftsStore((state) => state.clearSelection)
  const { archiveShifts, isPending } = useArchiveShiftsMutation()

  const targetShifts = deleteTarget ? shifts.filter((shift) => deleteTarget.includes(shift.id)) : []
  const shiftCount = targetShifts.length
  const peopleCount = new Set(
    targetShifts.flatMap((shift) => (shift.shiftSlots.nodes ?? []).map((slot) => slot.supporter?.id).filter(Boolean))
  ).size

  const shiftsPhrase = t('bulk_delete.shift_count', { count: shiftCount })
  const body =
    peopleCount > 0
      ? t('bulk_delete.body', { shifts: shiftsPhrase, volunteers: t('bulk_delete.volunteer_count', { count: peopleCount }) })
      : t('bulk_delete.body_empty', { shifts: shiftsPhrase })

  const handleConfirm = () => {
    if (!deleteTarget) return
    archiveShifts({
      ids: deleteTarget,
      onSuccess: () => {
        closeBulkDelete()
        clearSelection()
        router.reload({ only: ['event'] })
      },
    })
  }

  return (
    <ConfirmDialog
      open={deleteTarget !== null}
      onOpenChange={(open) => !open && closeBulkDelete()}
      title={t('bulk_delete.title', { count: shiftCount })}
      body={body}
      confirmLabel={t('bulk_delete.confirm', { count: shiftCount })}
      confirmColor="danger"
      confirmIcon="trash-01"
      onConfirm={handleConfirm}
      busy={isPending}
    >
      <AppErrorsAlert errorsPath="rosterAssignment" />
    </ConfirmDialog>
  )
}
